/* MyBoost product page — UGC story viewer (ES module).
   Nothing plays on page load: a video is inserted from its <template> only after a tap.
   Advancing to the next video at `ended` happens only while the visitor keeps the viewer open. */
export const SWIPE_CLOSE_RATIO = 0.3;
export const HOLD_DELAY_MS = 200;

export function nextIndex(current, total, dir) {
  if (!Number.isInteger(total) || total <= 0) return -1;
  if (dir < 0) return Math.max(0, current - 1);
  return current + 1 < total ? current + 1 : -1;
}

export function progressRatio(currentTime, duration) {
  if (!Number.isFinite(currentTime) || !Number.isFinite(duration) || duration <= 0) return 0;
  return Math.min(1, Math.max(0, currentTime / duration));
}

export function shouldCloseOnSwipe(deltaY, height) {
  if (!Number.isFinite(deltaY) || !Number.isFinite(height) || height <= 0) return false;
  return deltaY >= height * SWIPE_CLOSE_RATIO;
}

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function setup(section) {
  if (section.dataset.mbaUgcReady === 'true') return;
  const dialog = section.querySelector('[data-mba-story]');
  if (!dialog || typeof dialog.showModal !== 'function') return;
  section.dataset.mbaUgcReady = 'true';
  const frame = dialog.querySelector('[data-mba-story-frame]');
  const header = dialog.querySelector('[data-mba-story-header]');
  const stage = dialog.querySelector('[data-mba-story-stage]');
  const sound = dialog.querySelector('[data-mba-story-action="sound"]');
  const bars = [...dialog.querySelectorAll('[data-mba-story-bar]')];
  const templates = [...section.querySelectorAll('[data-mba-ugc-template]')];
  const total = templates.length;
  let current = -1;
  let video = null;
  let muted = true;
  let paused = false;
  let trigger = null;
  let focusTarget = null;
  let gesture = null;
  let suppressClick = false;

  const setBar = (index, ratio) => {
    if (bars[index]) bars[index].style.transform = `scaleX(${ratio})`;
  };

  const syncSound = () => {
    if (video) video.muted = muted;
    sound.setAttribute('aria-label', muted ? sound.dataset.mbaLabelOn : sound.dataset.mbaLabelOff);
    sound.querySelector('[data-mba-story-muted]').hidden = !muted;
    sound.querySelector('[data-mba-story-unmuted]').hidden = muted;
  };

  // Visual only: a centred play icon while the current story is paused (hold, keyboard or tab hidden).
  const updatePausedUi = () => dialog.toggleAttribute('data-paused', paused);

  // Browsers can refuse an unmuted play() outside a direct user gesture, such as the auto-advance
  // to the next story. Only fall back to muted when that refusal is the actual reason; any other
  // refusal (or one already muted) shows the pause icon instead of stalling silently.
  const playVideo = (element) => {
    element.play().catch((error) => {
      if (video !== element) return;
      if (error?.name === 'NotAllowedError' && !muted) {
        muted = true;
        syncSound();
        element.play().catch(() => {
          if (video === element) { paused = true; updatePausedUi(); }
        });
        return;
      }
      paused = true;
      updatePausedUi();
    });
  };

  const setPaused = (next) => {
    if (!video || paused === next) return;
    paused = next;
    updatePausedUi();
    if (paused) video.pause();
    else playVideo(video);
  };

  const unload = () => {
    if (!video) return;
    video.pause();
    video.querySelectorAll('source').forEach((source) => source.remove());
    video.removeAttribute('src');
    video.load();
    video = null;
  };

  const go = (dir) => {
    const target = nextIndex(current, total, dir);
    if (target === -1) dialog.close();
    else show(target);
  };

  function show(index) {
    unload();
    current = index;
    paused = false;
    updatePausedUi();
    bars.forEach((bar, position) => setBar(position, position < index ? 1 : 0));
    const fragment = templates[index].content.cloneNode(true);
    header.replaceChildren(...fragment.querySelectorAll('[data-mba-story-who]'));
    stage.replaceChildren(fragment);
    const element = stage.querySelector('video');
    if (!element) return;
    video = element;
    element.playsInline = true;
    element.addEventListener('timeupdate', () => {
      if (video === element && !paused) setBar(index, progressRatio(element.currentTime, element.duration));
    });
    element.addEventListener('ended', () => {
      if (video !== element) return;
      setBar(index, 1);
      if (dialog.open && current === index && !paused && !reduceMotion()) go(1);
    });
    // The element's own pause/play events are the source of truth: they also fire when something
    // outside our own calls pauses it (e.g. the tab-hidden handler below), keeping `paused` in sync.
    // An end-of-media pause is ignored here so it never blocks the `ended` auto-advance below.
    element.addEventListener('pause', () => {
      if (video === element && !paused && !element.ended) { paused = true; updatePausedUi(); }
    });
    element.addEventListener('play', () => {
      if (video === element && paused) { paused = false; updatePausedUi(); }
    });
    syncSound();
    playVideo(element);
  }

  const open = (index, button) => {
    trigger = button;
    focusTarget = null;
    muted = true;
    document.documentElement.classList.add('mba-lock');
    if (!dialog.open) dialog.showModal();
    show(index);
  };

  dialog.addEventListener('close', () => {
    unload();
    header.replaceChildren();
    stage.replaceChildren();
    current = -1;
    paused = false;
    updatePausedUi();
    gesture = null;
    frame.classList.remove('is-dragging');
    frame.style.transform = '';
    document.documentElement.classList.remove('mba-lock');
    const target = focusTarget && document.contains(focusTarget) ? focusTarget : trigger;
    if (target && focusTarget === target) target.scrollIntoView({ block: 'center', behavior: reduceMotion() ? 'auto' : 'smooth' });
    if (target && document.contains(target)) target.focus({ preventScroll: true });
    focusTarget = null;
  });

  section.addEventListener('click', (event) => {
    const button = event.target.closest('[data-mba-ugc-open]');
    if (!button) return;
    const index = Number(button.dataset.mbaUgcOpen);
    if (Number.isInteger(index) && templates[index]) open(index, button);
  });

  dialog.addEventListener('click', (event) => {
    if (suppressClick) {
      suppressClick = false;
      event.stopPropagation();
      return;
    }
    const action = event.target.closest('[data-mba-story-action]');
    if (!action) return;
    const name = action.dataset.mbaStoryAction;
    if (name === 'close') dialog.close();
    else if (name === 'prev') go(-1);
    else if (name === 'next') go(1);
    else if (name === 'sound') {
      muted = !muted;
      syncSound();
    } else if (name === 'cta') {
      focusTarget = document.querySelector('[data-mba-product] [data-mba-account-input]');
      dialog.close();
    }
  });

  dialog.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      go(1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      go(-1);
    } else if (event.key === 'k' || event.key === 'K') {
      event.preventDefault();
      setPaused(!paused);
    } else if ((event.key === ' ' || event.key === 'Spacebar') && !event.target.closest('button')) {
      event.preventDefault();
      setPaused(!paused);
    }
  });

  // Hold-to-pause lives on the frame, not the stage: the tap zones, top bar and foot are stacked
  // above the stage and would otherwise swallow the gesture. A press under HOLD_DELAY_MS stays a
  // normal tap (zone navigation, sound, close, cta); pointer capture during a swipe still delivers
  // the release to the frame, so resuming on release/cancel below also covers a cancelled swipe.
  // The hold timer itself only arms over the stage or the prev/next zones, never over the
  // Close/Sound/CTA buttons, so those keep their normal single-tap behaviour undelayed.
  frame.addEventListener('pointerdown', (event) => {
    if (!event.isPrimary || event.button > 0) return;
    suppressClick = false;
    const id = event.pointerId;
    const holdable = !event.target.closest('button') || event.target.closest('.mba-pstory__zone');
    gesture = { id, x: event.clientX, y: event.clientY, dy: 0, dragging: false, held: false, timer: 0 };
    if (holdable) {
      gesture.timer = setTimeout(() => {
        if (!gesture || gesture.id !== id || gesture.dragging || !video) return;
        gesture.held = true;
        setPaused(true);
      }, HOLD_DELAY_MS);
    }
  });

  frame.addEventListener('pointermove', (event) => {
    if (!gesture || event.pointerId !== gesture.id) return;
    const dx = event.clientX - gesture.x;
    const dy = event.clientY - gesture.y;
    if (!gesture.dragging && dy > 12 && dy > Math.abs(dx)) {
      gesture.dragging = true;
      clearTimeout(gesture.timer);
      frame.classList.add('is-dragging');
      frame.setPointerCapture?.(event.pointerId);
    }
    if (!gesture.dragging) return;
    gesture.dy = Math.max(0, dy);
    if (!reduceMotion()) frame.style.transform = `translateY(${gesture.dy}px)`;
  });

  const endGesture = (event, cancelled) => {
    if (!gesture || event.pointerId !== gesture.id) return;
    const { dragging, held, dy } = gesture;
    clearTimeout(gesture.timer);
    gesture = null;
    if (dragging) {
      frame.classList.remove('is-dragging');
      frame.style.transform = '';
      if (!cancelled && shouldCloseOnSwipe(dy, frame.clientHeight)) dialog.close();
    }
    // suppressClick is cleared by the click that follows (dialog click handler below) or,
    // failing that, by the next pointerdown above — never by a timer, which can race the click.
    if (held) suppressClick = true;
    if (dialog.open) setPaused(false);
  };
  frame.addEventListener('pointerup', (event) => endGesture(event, false));
  frame.addEventListener('pointercancel', (event) => endGesture(event, true));
}

export function initUgc(scope = document) {
  scope.querySelectorAll('[data-mba-ugc]').forEach(setup);
}

if (typeof document !== 'undefined') {
  initUgc();
  document.addEventListener('shopify:section:load', (event) => initUgc(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.querySelector?.('[data-mba-story][open]')) document.documentElement.classList.remove('mba-lock');
  });
  // One module-level listener for every story viewer instead of one per setup() call, so
  // re-rendering a section in the theme editor never accumulates duplicate listeners.
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) return;
    document.querySelectorAll('[data-mba-story] video').forEach((element) => element.pause());
  });
}
